<template>
  <iControlDock
    ref="dock"
    :open="Boolean(openDrawer)"
    :drawer-key="renderedDrawer || ''"
    data-test="prejoin-controls"
    :data-open-drawer="openDrawer || 'none'"
  >
    <template v-if="renderedDrawer" #drawer>
      <CallDeviceDrawer
        ref="deviceDrawer"
        :kind="renderedDrawer"
        picker-test-id-prefix="prejoin"
        :show-settings="false"
        :available-audio-input-devices="availableAudioInputDevices"
        :available-output-devices="availableOutputDevices"
        :available-video-devices="availableVideoDevices"
        :selected-microphone-device-id="selectedMicrophoneDeviceId"
        :selected-output-device-id="selectedOutputDeviceId"
        :selected-camera-device-id="selectedCameraDeviceId"
        @change-microphone="$emit('change-microphone', $event)"
        @change-output="$emit('change-output', $event)"
        @change-camera="$emit('change-camera', $event)"
        @open-settings="openSettingsFromDrawer"
      />
    </template>

    <iSplitControlButton
      :label="isMicrophoneEnabled ? 'Mute microphone' : 'Unmute microphone'"
      menu-label="Audio settings"
      indicator="activity"
      :muted="!isMicrophoneEnabled"
      :expanded="openDrawer === 'microphone'"
      :speaking="isSpeaking"
      data-test="call-control-mic"
      :data-muted="String(!isMicrophoneEnabled)"
      @click="$emit('toggle-mic')"
      @toggle="toggleDrawer('microphone')"
    >
      <iIcon :name="isMicrophoneEnabled ? 'ic:baseline-mic' : 'ic:baseline-mic-off'" variant="inherit" />
    </iSplitControlButton>

    <iSplitControlButton
      :label="isCameraEnabled ? 'Turn camera off' : 'Turn camera on'"
      menu-label="Video settings"
      :muted="!isCameraEnabled"
      :expanded="openDrawer === 'camera'"
      data-test="call-control-camera"
      :data-muted="String(!isCameraEnabled)"
      @click="$emit('toggle-camera')"
      @toggle="toggleDrawer('camera')"
    >
      <iIcon :name="isCameraEnabled ? 'ic:baseline-videocam' : 'ic:baseline-videocam-off'" variant="inherit" />
    </iSplitControlButton>

    <CallControlButton
      :is-toggle="false"
      label="Audio and video settings"
      data-test="prejoin-settings"
      @click="openSettingsFromDrawer"
    >
      <iIcon name="ic:outline-settings" variant="inherit" />
    </CallControlButton>
  </iControlDock>
</template>

<script>
import CallControlButton from "%/apps/video-engine/components/video-call/controls/CallControlButton.vue";
import CallDeviceDrawer from "%/apps/video-engine/components/video-call/controls/CallDeviceDrawer.vue";

const DRAWERS = ["microphone", "camera"];
const DRAWER_COLLAPSE_MS = 160;

export default {
  name: "PreJoinDock",
  components: { CallControlButton, CallDeviceDrawer },
  props: {
    isMicrophoneEnabled: {
      type: Boolean,
      default: false,
    },
    isCameraEnabled: {
      type: Boolean,
      default: false,
    },
    isSpeaking: {
      type: Boolean,
      default: false,
    },
    availableAudioInputDevices: {
      type: Array,
      default: () => [],
    },
    availableOutputDevices: {
      type: Array,
      default: () => [],
    },
    availableVideoDevices: {
      type: Array,
      default: () => [],
    },
    selectedMicrophoneDeviceId: {
      type: String,
      default: null,
    },
    selectedOutputDeviceId: {
      type: String,
      default: null,
    },
    selectedCameraDeviceId: {
      type: String,
      default: null,
    },
  },
  emits: ["toggle-mic", "toggle-camera", "change-microphone", "change-output", "change-camera", "open-settings"],
  data() {
    return {
      openDrawer: null,
      renderedDrawer: null,
    };
  },
  mounted() {
    window.addEventListener("keydown", this.closeOnEscape);
    window.addEventListener("pointerdown", this.closeDrawerOnOutsidePointer, true);
  },
  beforeUnmount() {
    window.clearTimeout(this.drawerCollapseTimer);
    window.removeEventListener("keydown", this.closeOnEscape);
    window.removeEventListener("pointerdown", this.closeDrawerOnOutsidePointer, true);
  },
  methods: {
    toggleDrawer(drawer) {
      if (!DRAWERS.includes(drawer)) {
        throw new Error(`PreJoinDock: unknown drawer ${JSON.stringify(drawer)}; expected one of ${DRAWERS.join(", ")}`);
      }
      if (this.openDrawer === drawer) {
        this.closeDrawer();
        return;
      }
      window.clearTimeout(this.drawerCollapseTimer);
      this.openDrawer = drawer;
      this.renderedDrawer = drawer;
    },
    closeDrawer() {
      if (!this.openDrawer) {
        return;
      }
      this.openDrawer = null;
      window.clearTimeout(this.drawerCollapseTimer);
      this.drawerCollapseTimer = window.setTimeout(() => {
        if (!this.openDrawer) {
          this.renderedDrawer = null;
        }
      }, DRAWER_COLLAPSE_MS);
    },
    closeOnEscape(event) {
      if (event.key !== "Escape" || !this.openDrawer) {
        return;
      }
      const openPicker = this.$refs.deviceDrawer?.pickers().find(picker => picker.isOpen);
      if (openPicker) {
        openPicker.close();
        return;
      }
      this.closeDrawer();
    },
    closeDrawerOnOutsidePointer(event) {
      if (!this.openDrawer) {
        return;
      }
      const target = event.target;
      if (this.$refs.dock?.$el?.contains(target) || target?.closest?.(".i-dropdown-content")) {
        return;
      }
      this.closeDrawer();
    },
    openSettingsFromDrawer() {
      this.closeDrawer();
      this.$emit("open-settings");
    },
  },
};
</script>
